import React, { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, X, Image as ImageIcon, AlertCircle, Plus, Trash2 } from "lucide-react";
import { uploadToCloudinary } from "../../../utils/cloudinary";
import Swal from "sweetalert2";

const ServiceUpdateModal = ({ onClose, service, onSuccess, categories }) => {
  const [formData, setFormData] = useState(service || {});
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubCategory, setSelectedSubCategory] = useState("");
  const [subCategories, setSubCategories] = useState([]);
  const [mainFile, setMainFile] = useState(null);
  const [subFile, setSubFile] = useState(null);
  const [images, setImages] = useState({
    mainImage: null,
    subImage: null,
  });
  const [featurePreviews, setFeaturePreviews] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (service) {
      setFormData(service);
      setSelectedCategory(service.category || "");
      setSelectedSubCategory(service.subCategory || "");
      if (service.category) {
        fetchSubCategories(service.category).then(() => {
          setSelectedSubCategory(service.subCategory || "");
        });
      }
    }
  }, [service]);

  useEffect(() => {
    if (selectedCategory && selectedCategory !== (service?.category || "")) {
      fetchSubCategories();
      setSelectedSubCategory("");
    }
  }, [selectedCategory]);

  const fetchSubCategories = async (catId) => {
    try {
      const id = catId || selectedCategory;
      if (!id) return;
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/subcategory/get-by-id/${id}`,
        // { withCredentials: true }
      );
      setSubCategories(response.data);
    } catch (err) {
      console.error("Error fetching subcategories:", err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
  };

  const handleSubCategoryChange = (e) => {
    setSelectedSubCategory(e.target.value);
  };

  const handleSubDescriptionChange = (index, value) => {
    const newSubDesc = [...formData.subDescription];
    newSubDesc[index] = value;
    setFormData((prev) => ({ ...prev, subDescription: newSubDesc }));
  };

  const addSubDescription = () => {
    setFormData((prev) => ({ ...prev, subDescription: [...prev.subDescription, ""] }));
  };

  const removeSubDescription = (index) => {
    const newSubDesc = formData.subDescription.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, subDescription: newSubDesc.length > 0 ? newSubDesc : [""] }));
  };

  const handleFeatureChange = (index, field, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index][field] = value;
    setFormData((prev) => ({ ...prev, features: newFeatures }));
  };

  const handleFeatureImage = (index, file) => {
    if (file) {
      const newFeatures = [...formData.features];
      newFeatures[index].image = file;
      setFormData((prev) => ({ ...prev, features: newFeatures }));

      const reader = new FileReader();
      reader.onloadend = () => {
        setFeaturePreviews((prev) => ({ ...prev, [index]: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const addFeature = () => {
    const newIndex = formData.features.length;
    setFormData((prev) => ({
      ...prev,
      features: [...prev.features, { title: "", description: "", image: null }],
    }));
    setFeaturePreviews((prev) => ({ ...prev, [newIndex]: null }));
  };

  const removeFeature = (index) => {
    const newFeatures = formData.features.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      features: newFeatures.length > 0 ? newFeatures : [{ title: "", description: "", image: null }],
    }));
    setFeaturePreviews((prev) => {
      const newPrev = { ...prev };
      delete newPrev[index];
      return newPrev;
    });
  };

  const handleFaqChange = (index, field, value) => {
    const newFaqs = [...formData.faqs];
    newFaqs[index][field] = value;
    setFormData((prev) => ({ ...prev, faqs: newFaqs }));
  };

  const addFaq = () => {
    setFormData((prev) => ({ ...prev, faqs: [...prev.faqs, { question: "", answer: "" }] }));
  };

  const removeFaq = (index) => {
    const newFaqs = formData.faqs.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, faqs: newFaqs.length > 0 ? newFaqs : [{ question: "", answer: "" }] }));
  };

  const handleMainImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMainFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImages((prev) => ({ ...prev, mainImage: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSubFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImages((prev) => ({ ...prev, subImage: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const validSubDesc = formData.subDescription.filter(d => d.trim() !== "");
    const validFeatures = formData.features.filter(f => f.title.trim() && f.description.trim());
    const validFaqs = formData.faqs.filter(faq => faq.question.trim() && faq.answer.trim());
    if (
      !selectedCategory ||
      //   !selectedSubCategory ||
      !formData.title.trim() ||
      !formData.description.trim() ||
      !formData.subTitle.trim() ||
      validSubDesc.length === 0 ||
      validFeatures.length === 0 ||
      validFaqs.length === 0
    ) {
      return "Please fill in all required fields. Ensure arrays have at least one valid entry.";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let mainUrl = formData.image;
      let subUrl = formData.subImage;
      if (mainFile) mainUrl = await uploadToCloudinary(mainFile);
      if (subFile) subUrl = await uploadToCloudinary(subFile);
      const featureUrlsPromises = formData.features
        .filter(f => f.image && f.image instanceof File)
        .map(f => uploadToCloudinary(f.image));
      const newFeatureUrls = await Promise.all(featureUrlsPromises);
      let urlIndex = 0;
      const validFeatures = formData.features
        .filter(f => f.title.trim() && f.description.trim())
        .map(f => ({
          title: f.title.trim(),
          description: f.description.trim(),
          image: f.image instanceof File ? newFeatureUrls[urlIndex++] : f.image,
        }));
      const validSubDesc = formData.subDescription.filter(d => d.trim() !== "");
      const validFaqs = formData.faqs.filter(faq => faq.question.trim() && faq.answer.trim());

      const submitData = {
        category: selectedCategory,
        subCategory: selectedSubCategory,
        title: formData.title.trim(),
        description: formData.description.trim(),
        subTitle: formData.subTitle.trim(),
        subDescription: validSubDesc,
        features: validFeatures,
        faqs: validFaqs,
        image: mainUrl,
        subImage: subUrl,
      };

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/service/update-service/${service._id}`,
        submitData,
        // {
        //   withCredentials: true,
        //   headers: { "Content-Type": "application/json" },
        // }
      );

      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Service updated successfully!',
          timer: 2000,
          showConfirmButton: false,
        });
        onSuccess();
        onClose();
      }
    } catch (err) {
      console.error("Error updating service:", err);
      setError(err.response?.data?.message || "Failed to update service");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-[#0bb6d9] to-[#3cd7f6] text-white p-6 flex justify-between items-center rounded-t-2xl">
          <h3 className="text-xl font-bold text-white">Update Service</h3>
          <button
            onClick={onClose}
            className="hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              {error}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Category *</label>
              <select
                value={selectedCategory}
                onChange={handleCategoryChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0bb6d9] focus:border-transparent outline-none transition"
                required
              >
                <option value="">Select Category</option>
                {categories.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.title}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">SubCategory *</label>
              <select
                value={selectedSubCategory}
                onChange={handleSubCategoryChange}
                disabled={!selectedCategory}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0bb6d9] focus:border-transparent outline-none transition disabled:opacity-50"

              >
                <option value="">Select SubCategory</option>
                {subCategories.map((subCat) => (
                  <option key={subCat._id} value={subCat._id}>
                    {subCat.title}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title || ""}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0bb6d9] focus:border-transparent outline-none transition"
              placeholder="Enter service title"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Sub Title *</label>
            <input
              type="text"
              name="subTitle"
              value={formData.subTitle || ""}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0bb6d9] focus:border-transparent outline-none transition"
              placeholder="Enter sub title"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Description *</label>
            <textarea
              name="description"
              value={formData.description || ""}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0bb6d9] focus:border-transparent outline-none transition resize-none"
              placeholder="Enter service description"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Sub Descriptions * (at least one)</label>
            <div className="space-y-3">
              {(formData.subDescription || []).map((desc, index) => (
                <div key={index} className="flex items-start gap-2">
                  <input
                    type="text"
                    value={desc || ""}
                    onChange={(e) => handleSubDescriptionChange(index, e.target.value)}
                    placeholder={`Sub description ${index + 1}`}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0bb6d9] focus:border-transparent outline-none transition"
                  />
                  <button
                    type="button"
                    onClick={() => removeSubDescription(index)}
                    className="p-2 text-red-500 hover:text-red-700 transition-colors"
                    disabled={(formData.subDescription || []).length === 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addSubDescription}
                className="flex items-center gap-2 text-[#0bb6d9] font-semibold hover:text-[#069ab7] transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Sub Description
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Features * (at least one, each with image)</label>
            <div className="space-y-4">
              {(formData.features || []).map((feature, index) => (
                <div key={index} className="border p-4 rounded-lg space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Feature Title *</label>
                    <input
                      type="text"
                      value={feature.title || ""}
                      onChange={(e) => handleFeatureChange(index, "title", e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#0bb6d9] outline-none"
                      placeholder="Enter feature title"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                    <textarea
                      value={feature.description || ""}
                      onChange={(e) => handleFeatureChange(index, "description", e.target.value)}
                      rows={2}
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#0bb6d9] outline-none resize-none"
                      placeholder="Enter description"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
                    <input
                      type="file"
                      onChange={(e) => handleFeatureImage(index, e.target.files[0])}
                      accept="image/*"
                      className="w-full text-sm text-gray-500 file:mr-4 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-[#0bb6d9] file:text-white hover:file:bg-[#069ab7]"
                    />
                    {featurePreviews[index] && (
                      <img
                        src={featurePreviews[index]}
                        alt={`Preview ${index}`}
                        className="mt-2 w-20 h-20 object-cover rounded border"
                      />
                    )}
                    {feature.image && !featurePreviews[index] && (
                      <img
                        src={feature.image}
                        alt={`Current ${index}`}
                        className="mt-2 w-20 h-20 object-cover rounded border"
                      />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFeature(index)}
                    className="p-2 text-red-500 hover:text-red-700 transition-colors float-right"
                    disabled={formData.features.length === 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addFeature}
                className="flex items-center gap-2 text-[#0bb6d9] font-semibold hover:text-[#069ab7] transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Feature
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">FAQs * (at least one)</label>
            <div className="space-y-4">
              {(formData.faqs || []).map((faq, index) => (
                <div key={index} className="border p-4 rounded-lg space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Question *</label>
                    <input
                      type="text"
                      value={faq.question || ""}
                      onChange={(e) => handleFaqChange(index, "question", e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#0bb6d9] outline-none"
                      placeholder="Enter question"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Answer *</label>
                    <textarea
                      value={faq.answer || ""}
                      onChange={(e) => handleFaqChange(index, "answer", e.target.value)}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#0bb6d9] outline-none resize-none"
                      placeholder="Enter answer"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFaq(index)}
                    className="p-2 text-red-500 hover:text-red-700 transition-colors float-right"
                    disabled={formData.faqs.length === 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addFaq}
                className="flex items-center gap-2 text-[#0bb6d9] font-semibold hover:text-[#069ab7] transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add FAQ
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Main Image</label>
            <input
              type="file"
              name="mainImage"
              onChange={handleMainImageChange}
              accept="image/*"
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#0bb6d9] file:text-white hover:file:bg-[#069ab7] transition"
            />
            {images.mainImage ? (
              <img src={images.mainImage} alt="Main Preview" className="mt-4 w-32 h-32 object-cover rounded-lg border" />
            ) : (
              formData.image && <img src={formData.image} alt="Current Main" className="mt-4 w-32 h-32 object-cover rounded-lg border" />
            )}
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Sub Image</label>
            <input
              type="file"
              name="subImage"
              onChange={handleSubImageChange}
              accept="image/*"
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#0bb6d9] file:text-white hover:file:bg-[#069ab7] transition"
            />
            {images.subImage ? (
              <img src={images.subImage} alt="Sub Preview" className="mt-4 w-32 h-32 object-cover rounded-lg border" />
            ) : (
              formData.subImage && <img src={formData.subImage} alt="Current Sub" className="mt-4 w-32 h-32 object-cover rounded-lg border" />
            )}
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 text-gray-700 bg-gray-100 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-[#0bb6d9] text-white rounded-lg font-semibold hover:bg-[#069ab7] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Updating...
                </>
              ) : (
                "Update"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServiceUpdateModal;